import { effect } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { Game } from "./components/Game";
import { HowToPlay } from "./components/HowToPlay";
import { NextPuzzleCountdown } from "./components/NextPuzzleCountdown";
import { PlayerRibbon } from "./components/PlayerRibbon";
import { PuzzleStatus } from "./components/PuzzleStatus";
import { ShareGrid } from "./components/ShareGrid";
import { TopBar } from "./components/TopBar";
import { Keyboard } from "./components/Keyboard";
import { MastheadParticles } from "./effects/MastheadParticles";
import {
  WIN_SEQUENCE_FIRE_LEAD_MS,
  WIN_SEQUENCE_TOTAL_MS,
} from "./effects/mastheadConfigs";
import * as gameState from "./game/state";
import { fireCelebration, loadError, status } from "./game/state";

/** How long after a live playing → won transition we hold the bare
    keyboard before flipping to the keyboard + countdown split. The
    fire-breath sequence runs from `FIRE_LEAD_MS` to
    `FIRE_LEAD_MS + TOTAL_MS` after the trigger; we add a beat past
    its end so the player gets to enjoy the celebration before the UI
    nudges them toward "next puzzle". */
const POST_WIN_KEYBOARD_SPLIT_DELAY_MS =
  WIN_SEQUENCE_FIRE_LEAD_MS + WIN_SEQUENCE_TOTAL_MS + 1000;

export function App() {
  // Layout-priority guard: when the playfield can't show every guess
  // row, the share grid yields first. We mark .app with the
  // app--middle-clipped class while .app__middle is overflowing
  // internally; the CSS rule in styles/components/game.css then pins
  // .sharegrid__rows at its 3-row floor (with internal scroll) so
  // every freed pixel falls to the board. When the board does fit, we
  // drop the marker and the share grid is allowed to grow back toward
  // its 5-row ceiling.
  //
  // The "force-remove, measure, conditionally re-add" pattern gives a
  // stable fixpoint: in viewports where 5-row would clip the board but
  // 3-row wouldn't, the marker stays on (re-applied each cycle); in
  // viewports where even 5-row fits, the marker stays off; in
  // viewports where even 3-row can't make the board fit, the marker
  // stays on. ResizeObserver on .app__middle catches viewport changes
  // and ShareGrid-driven bottom-region resizes alike.
  useEffect(() => {
    const app = document.querySelector<HTMLElement>(".app");
    const middle = document.querySelector<HTMLElement>(".app__middle");
    if (app == null || middle == null) return;
    const evaluate = () => {
      app.classList.remove("app--middle-clipped");
      // Read scrollHeight to force a synchronous layout in the
      // un-clipped state before we ask about overflow.
      if (middle.scrollHeight > middle.clientHeight + 1) {
        app.classList.add("app--middle-clipped");
      }
    };
    evaluate();
    const ro = new ResizeObserver(evaluate);
    ro.observe(middle);
    return () => ro.disconnect();
  }, []);

  // Click-to-fire-celebration is a pre-deploy testing affordance only —
  // production renders the masthead as plain art so a stray tap during
  // play can't trigger the win sequence.
  const mastheadStage = import.meta.env.DEV ? (
    <button
      type="button"
      class="app__masthead-stage"
      onClick={() => fireCelebration()}
      aria-label="Play celebration animation"
      tabIndex={-1}
    >
      <img
        class="app__masthead"
        src="/masthead.jpg"
        width="1600"
        height="640"
        alt="PalinDragon"
      />
      <MastheadParticles />
    </button>
  ) : (
    <div class="app__masthead-stage app__masthead-stage--static">
      <img
        class="app__masthead"
        src="/masthead.jpg"
        width="1600"
        height="640"
        alt="PalinDragon"
      />
      <MastheadParticles />
    </div>
  );
  // When the loader has failed (no puzzle today, API down, missing-text
  // wire mismatch), the play surface collapses to just the "Sorry! No
  // puzzle today." placeholder — the toolbar above and the footer
  // below stay, but the reveal-buttons / hint / status TopBar, the
  // keyboard, and the share grid all hide. Game elements have nothing
  // to drive without a puzzle, so showing them invites confusion.
  const failed = loadError.value != null;
  const playing = status.value === "playing";

  // Hold the bare keyboard for the duration of the win celebration
  // (plus a one-second beat) before flipping to the split view. We
  // listen via @preact/signals' `effect()` so the callback fires
  // synchronously on the same status flip the celebration responds
  // to — and we can read `gameState.isHydrating` while it's still
  // true inside the loader's `batch()`, so a saved-and-restored win
  // never plays the celebration *or* the grace period.
  const [winCelebrationActive, setWinCelebrationActive] = useState(false);
  useEffect(() => {
    let prev = status.peek();
    let timer: number | null = null;
    const stop = effect(() => {
      const curr = status.value;
      const hydrating = gameState.isHydrating;
      const wasPrev = prev;
      prev = curr;
      if (hydrating) return;
      if (wasPrev === "playing" && curr === "won") {
        setWinCelebrationActive(true);
        if (timer != null) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          setWinCelebrationActive(false);
          timer = null;
        }, POST_WIN_KEYBOARD_SPLIT_DELAY_MS);
      }
    });
    return () => {
      stop();
      if (timer != null) window.clearTimeout(timer);
    };
  }, []);
  const showSplit = !playing && !winCelebrationActive;
  return (
    <div class="app">
      <PlayerRibbon
        className="app__top"
        headerClassName="app__header"
        toolbarClassName="app__toolbar"
        toolbar={
          <>
            <HowToPlay />
            <PuzzleStatus />
          </>
        }
        headerAfter={
          <h1 class="app__title sr-only">PalinDragon</h1>
        }
      >
        <div class="app__hero">
          {mastheadStage}
          {!failed && <TopBar />}
        </div>
      </PlayerRibbon>
      <main class="app__middle">
        <Game />
      </main>
      {!failed && (
        <div class="app__bottom">
          {showSplit ? (
            <div class="app__keyboard-row">
              <Keyboard />
              <NextPuzzleCountdown />
            </div>
          ) : (
            <Keyboard />
          )}
          <ShareGrid />
        </div>
      )}
      <footer class="app__footer">
        PalinDragon™ — The daily palindrome word game by{" "}
        <a
          href="https://lockedlab.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Locked Lab
        </a>
        {" • "}
        <a href="mailto:palindragon@lockedlab.com?subject=PalinDragon%20Feedback">
          Send us feedback!
        </a>
      </footer>
    </div>
  );
}
